/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { Modal, Offcanvas } from 'react-bootstrap';
import { useState, useMemo, useEffect, ChangeEvent } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Filter from './filter/Filter';
import NotInLists from './NotInLists';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import Button from 'components/base/Button';
import UpdateQuantity from './UpdateQuantity';
import inventoryItemService from 'service/inventoryItem';
import inventoryService from 'service/inventory';
import { showAlert } from 'store/alert/alert.thunk';
import SupperModal from 'components/modals/SupperModal';
import './styles.scss';
import { useAppContext } from 'providers/AppProvider';
import SearchBox from 'components/common/SearchBox';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';

export const createInventoryBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'inventory',
    url: '/inventory/create-inventory'
  },
  {
    label: 'create_inventory',
    url: '/inventory/create-inventory'
  },
  {
    label: 'new',
    active: true
  }
];

export type CreateInventoryData = {
  product_name: string;
  barcode: string;
  units: string;
  currency: string;
  cost: string;
  accounts_number: string;
  quantity_fact: string;
  shortage: string;
  surplus: string;
  deficiency_amount: string;
  surplus_amount: string;
  status: string;
  guid: string;
  gupid: number;
  package_quantity: string;
  measure_unit_kind_id: string;
};

const CreateInventory = () => {
  const { t, i18n } = useTranslation();
  const {
    config: { theme }
  } = useAppContext();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inventoryId = pathname.split('/').pop();

  const [openNotInList, setOpenNotInList] = useState(false);
  const [openAmount, setOpenAmount] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState({
    pid: 0,
    quantity: '',
    package_quantity: '',
    measure_unit_kind_id: ''
  });
  const [tableData, setTableData] = useState<any>({});
  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedInventoryId, setSelectedInventoryId] = useState<any>(null);
  const [openFinishSupperModal, setOpenFinishSupperModal] = useState(false);
  const [confirmToFinish, setConfirmToFinish] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedInventoryBarcode, setSelectedInventoryBarcode] = useState<
    string | null
  >(null);

  const dispatch: Dispatch<any> = useDispatch();

  //Get Inventory Item
  const {
    data,
    refetch: refetchInventoryItem,
    isLoading,
    isFetching
  } = useQuery(
    ['GET_INVENTORY_ITEM', inventoryId, currentPage],
    () => {
      if (inventoryId) {
        const res = inventoryItemService
          .getInventoryItemById(inventoryId, {
            limit: 10,
            offset: pageToOffset(currentPage, 10)
          })
          .then((res: any) => res);

        return res;
      }
    },
    {
      enabled: !!inventoryId
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const inventoryItemData: CreateInventoryData[] = useMemo(() => {
    const safeValue = (value: any) =>
      value === undefined || value === null ? 0 : value;
    const res =
      data?.inventory_items?.map((items: any) => {
        if (items && items?.product?.barcode) {
          const tableDataUpdate = {
            [items?.product?.barcode]: {
              quantity_fact: items?.quantity_actual ?? 0,
              gupid: items?.pid || 0
            }
          };

          setTableData((prevTableData: any) => ({
            ...prevTableData,
            ...tableDataUpdate
          }));
        }

        const packageQuantity = items?.nomenclature?.package_quantity || 1;

        const totalQuantity = items?.quantity
          ? Number(items?.quantity.toFixed(3))
          : 0;

        const fullPackagesTotal = Math.floor(totalQuantity / packageQuantity);
        const remainingQuantityTotal = Math.floor(
          totalQuantity % packageQuantity
        );
        const quantityDisplayTotal = `${fullPackagesTotal}/${remainingQuantityTotal}`;

        const quantity = items?.quantity_actual
          ? Number(items?.quantity_actual.toFixed(3))
          : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = Math.floor(quantity % packageQuantity);
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        /* prettier-ignore */
        const packageDeficencyTotal =
          items?.nomenclature?.measure_unit_kind_id ===
          '3c7cee55-ad41-4ee3-a409-a7bb5f428b36'
            ? Math.max(
                safeValue(items?.quantity) - safeValue(items?.quantity_actual),
                0
              ) *
              (safeValue(items?.price_in) / packageQuantity)
            : Math.max(
                safeValue(items?.quantity) - safeValue(items?.quantity_actual),
                0
              ) * safeValue(items?.price_in);

        /* prettier-ignore */
        const packageSurplusTotal =
            items?.nomenclature?.measure_unit_kind_id ===
          '3c7cee55-ad41-4ee3-a409-a7bb5f428b36'
            ? Math.max(
                safeValue(items?.quantity_actual) - safeValue(items?.quantity),
                0
              ) *
              (safeValue(items?.price_in) / packageQuantity)
            : Math.max(
                safeValue(items?.quantity_actual) - safeValue(items?.quantity),
                0
              ) * safeValue(items?.price_in);

        return {
          product_name: items?.product?.name,
          barcode: items?.product?.barcode,
          units: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          cost: safeValue(items?.price_in),
          measure_unit_kind_id: items?.nomenclature?.measure_unit_kind_id,
          accounts_number:
            items?.nomenclature?.measure_unit_kind_id ===
            '3c7cee55-ad41-4ee3-a409-a7bb5f428b36'
              ? quantityDisplayTotal
              : safeValue(items?.quantity),
          quantity_fact:
            items?.nomenclature?.measure_unit_kind_id ===
            '3c7cee55-ad41-4ee3-a409-a7bb5f428b36'
              ? quantityDisplay
              : safeValue(items?.quantity_actual),
          shortage: Math.max(
            safeValue(items?.quantity) - safeValue(items?.quantity_actual),
            0
          ),
          surplus: Math.max(
            safeValue(items?.quantity_actual) - safeValue(items?.quantity),
            0
          ),
          deficiency_amount: packageDeficencyTotal,
          surplus_amount: packageSurplusTotal,
          package_quantity: items?.nomenclature?.package_quantity || 1,
          invoice_quantity: safeValue(items?.invoice_quantity),
          return_client_quantity: safeValue(items?.return_client_quantity),
          return_provider_quantity: safeValue(items?.return_provider_quantity),
          sale_quantity: safeValue(items?.sale_quantity),
          order_quantity: safeValue(items?.order_quantity),
          moving_quantity: safeValue(items?.moving_quantity),
          defective_minus_quantity: safeValue(items?.defective_minus_quantity),
          defective_plus_quantity: safeValue(items?.defective_plus_quantity),
          /* prettier-ignore */
          current_balance:
            safeValue(items?.quantity_actual) +
            safeValue(items?.invoice_quantity) +
            safeValue(items?.return_client_quantity) +
            safeValue(items?.return_provider_quantity) +
            safeValue(items?.sale_quantity) +
            safeValue(items?.order_quantity) +
            safeValue(items?.moving_quantity) +
            safeValue(items?.defective_minus_quantity) +
            safeValue(items?.defective_plus_quantity),
          status: items?.status?.name?.ru,
          guid: items?.id,
          gupid: items?.pid || 0,
          product_id: items?.product_id,
          product_pid: items?.product_pid || 0
        };
      }) ?? [];

    return res;
  }, [data, inventoryId, setTableData, t, i18n?.language]);

  const handleDelete = (id: any, barcode: any) => {
    setSelectedInventoryId(id);
    setOpenSupperModal(true);
    setSelectedInventoryBarcode(barcode);
  };

  const formatNumber = (num: any) => {
    return num
      ? new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
          useGrouping: true
        })
          .format(num)
          .replace(/,/g, ' ')
      : 0;
  };

  const createInventoryDataTableColumns: ColumnDef<CreateInventoryData>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div>
          <Button
            variant="hover"
            onClick={() =>
              handleDelete(
                rowData?.row?.original?.gupid,
                rowData?.row?.original?.barcode
              )
            }
          >
            <FeatherIcon
              icon="trash-2"
              className="cursor-pointer text-danger"
              size={20}
            />
          </Button>
        </div>
      )
    },
    {
      accessorKey: 'order_number',
      enableSorting: false,
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'units',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'cost',
      header: t('price'),
      cell: rowData => formatNumber(rowData.getValue()),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'accounts_number',
      header: t('quantity_accounting'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity_fact',
      header: t('number_facts'),
      cell: rowData => {
        const amount = String(rowData?.getValue());
        return (
          <div>
            <Button
              variant="primary"
              onClick={() => {
                setOpenAmount(true);
                setSelectedQuantity({
                  pid: rowData?.row?.original?.gupid,
                  quantity: rowData?.row?.original?.quantity_fact,
                  package_quantity: rowData?.row?.original?.package_quantity,
                  measure_unit_kind_id:
                    rowData?.row?.original?.measure_unit_kind_id
                });
              }}
            >
              {amount}
            </Button>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'shortage',
      header: t('shortage'),

      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'surplus',
      header: t('excess'),
      cell: rowData => formatNumber(rowData.getValue()),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'deficiency_amount',
      header: t('amount_deficiency'),
      cell: rowData => formatNumber(rowData.getValue()),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'surplus_amount',
      header: t('amount_surplus'),
      cell: rowData => formatNumber(rowData.getValue()),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }

    // {
    //   accessorKey: 'invoice_quantity',
    //   header: t('purchase'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'return_client_quantity',
    //   header: t('customer_return'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'return_provider_quantity',
    //   header: t('supplier_return'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'sale_quantity',
    //   header: t('sale'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'order_quantity',
    //   header: t('order'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'moving_quantity',
    //   header: t('movements'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'defective_minus_quantity',
    //   header: t('marriage_goods_minus'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'defective_plus_quantity',
    //   header: t('marriage_goods_plus'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'current_balance',
    //   header: t('current_balance'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    // {
    //   accessorKey: 'status',
    //   header: t('status'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // }
  ];

  const table = useAdvanceTable({
    data: inventoryItemData,
    columns: createInventoryDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleFinish = () => {
    if (inventoryItemData?.length === 0) {
      dispatch(
        showAlert({
          title: t('product_not_added')
        })
      );
      setConfirmToFinish(false);
    }
    /* prettier-ignore */
    if (inventoryId && inventoryItemData?.length > 0) {
      inventoryService.getInventoryById(inventoryId).then((res: any) => {
        if (res) {
          const today = new Date();
          /* prettier-ignore */
          inventoryService
              .updateInventory(inventoryId, {
                ...res,
                status_id: 'af0fe0e3-3357-4b29-a33f-138c89cb75db',
                status_pid: 6,
                date_close: today.getTime()
              })
              .then((response: any) => {
                if (response) {
                  dispatch(
                    showAlert({
                      title: `${t("inventory")} ${t("successfully_added")}`,
                      type: 'success'
                    })
                  );
                  navigate('/inventory/create-inventory');
                }
              });
        }
      });
    }
  };

  const sumOfAllSurplus = inventoryItemData.reduce((acc: any, item: any) => {
    const allCost =
      item?.surplus_amount === undefined ? 0 : +item.surplus_amount;
    return acc + allCost;
  }, 0);

  const sumOfAllDeficiency = inventoryItemData.reduce((acc: any, item: any) => {
    const allCost =
      item?.deficiency_amount === undefined ? 0 : +item.deficiency_amount;
    return acc + allCost;
  }, 0);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  useEffect(() => {
    if (confirmToDelete && selectedInventoryId && selectedInventoryBarcode) {
      inventoryItemService
        .deleteInventoryItem(selectedInventoryId)
        .finally(() => {
          refetchInventoryItem();
          setConfirmToDelete(false);
          setSelectedInventoryId(null);
          setOpenSupperModal(false);
        });

      setTableData((prevTableData: any) => {
        const updatedTableData = { ...prevTableData };
        delete updatedTableData[selectedInventoryBarcode];
        return updatedTableData;
      });
    }
  }, [
    confirmToDelete,
    selectedInventoryId,
    refetchInventoryItem,
    selectedInventoryBarcode
  ]);

  useEffect(() => {
    if (confirmToFinish) {
      handleFinish();
    }
  }, [confirmToFinish]);

  return (
    <div>
      <PageBreadcrumb items={createInventoryBreadcrumbItems} />
      <div>
        <Filter
          refetchInventoryItem={refetchInventoryItem}
          tableData={tableData}
          setTableData={setTableData}
        />

        <SearchBox
          placeholder={t('search')}
          onChange={handleSearchInputChange}
          className="mt-3"
        />

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
            <AdvanceTable
              tableProps={{ className: 'phoenix-table fs-9' }}
              isLoading={loading}
            />
            <AdvanceTableCPagination
              count={pageCount}
              page={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </AdvanceTableProvider>

        <div
          className={
            theme === 'dark' ? 'dark-footer-inv-create' : 'footer-inv-create'
          }
        >
          <div className="box">
            <p className="mb-0 text-bold">{t('total_amount_shortages')}: </p>
            <span>{formatNumber(sumOfAllDeficiency)}</span>
          </div>

          <div className="box">
            <p className="mb-0 text-bold">{t('total_amount_surplus')}: </p>
            <span>{formatNumber(sumOfAllSurplus)}</span>
          </div>

          <div className="main-btn-group">
            <Button variant="primary" onClick={() => setOpenNotInList(true)}>
              {t('not_added_list')}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setOpenFinishSupperModal(true)}
            >
              {t('complete_invertization')}
            </Button>
          </div>
        </div>
      </div>
      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('added_products')}
        bodyText={t('you_want_delete')}
      />
      <SupperModal
        show={openFinishSupperModal}
        setShow={setOpenFinishSupperModal}
        setConfirm={setConfirmToFinish}
        title={t('complete_invertization')}
        bodyText={t('want_complete_inventory')}
      />
      <Modal
        show={openNotInList}
        onHide={() => setOpenNotInList(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('uninventorized_goods')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NotInLists
            refetchInventoryItem={refetchInventoryItem}
            setOpenNotInList={setOpenNotInList}
          />
        </Modal.Body>
      </Modal>
      <Offcanvas
        show={openAmount}
        onHide={() => setOpenAmount(false)}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('quantity')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <UpdateQuantity
            setOpenAmount={setOpenAmount}
            selectedQuantity={selectedQuantity}
            refetchInventoryItem={refetchInventoryItem}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default CreateInventory;
